import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Mail, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { sendEmailVerificationEmail, getCurrentUser } from '../firebase/auth';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
  padding: 2rem;
`;

const Card = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 3rem;
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.8);
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: rgba(255, 107, 53, 0.1);
  border-radius: 50%;
  margin: 0 auto 2rem;
  color: #ff6b35;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: #cccccc;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const EmailAddress = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  margin: 1.5rem 0;
  color: #ff6b35;
  font-weight: 500;
  font-size: 1.1rem;
`;

const Button = styled.button`
  background: #ff6b35;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 1rem auto;
  min-width: 200px;

  &:hover {
    background: #e55a2b;
    transform: translateY(-2px);
  }

  &:disabled {
    background: #666666;
    cursor: not-allowed;
    transform: none;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #888888;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 0.75rem 2rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 1rem auto;
  min-width: 200px;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #ffffff;
  }
`;

const StatusMessage = styled.div`
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const SuccessMessage = styled(StatusMessage)`
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
  border: 1px solid rgba(34, 197, 94, 0.2);
`;

const ErrorMessage = styled(StatusMessage)`
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
`;

const InfoMessage = styled(StatusMessage)`
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.2);
`;

const Instructions = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1.5rem;
  margin: 2rem 0;
  text-align: left;
`;

const InstructionTitle = styled.h3`
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const InstructionList = styled.ol`
  color: #cccccc;
  line-height: 1.6;
  padding-left: 1.5rem;
  
  li {
    margin-bottom: 0.5rem;
  }
`;

const EmailVerificationPending = ({ userEmail, onVerificationComplete }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [checkingVerification, setCheckingVerification] = useState(false);

  const handleResendVerification = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      const user = getCurrentUser();
      if (!user) {
        throw new Error('No user found');
      }
      
      await sendEmailVerificationEmail(user);
      setMessage('Verification email sent! Please check your inbox.');
      setMessageType('success');
    } catch (error) {
      console.error('Error sending verification email:', error);
      setMessage('Failed to send verification email. Please try again.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const checkEmailVerification = async () => {
    setCheckingVerification(true);
    
    try {
      const user = getCurrentUser();
      if (!user) {
        throw new Error('No user found');
      }
      
      // Reload user to get updated email verification status
      await user.reload();
      
      if (user.emailVerified) {
        setMessage('Email verified successfully! Redirecting...');
        setMessageType('success');
        
        // Force a token refresh to get updated email verification status
        try {
          await user.getIdToken(true);
          console.log('✅ EmailVerificationPending: Token refreshed after email verification');
        } catch (tokenError) {
          console.warn('⚠️ EmailVerificationPending: Error refreshing token:', tokenError);
        }
        
        // Call the completion callback after a short delay
        setTimeout(() => {
          onVerificationComplete();
        }, 2000);
      } else {
        setMessage('Email not yet verified. Please check your inbox and click the verification link.');
        setMessageType('info');
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
      setMessage('Error checking verification status. Please try again.');
      setMessageType('error');
    } finally {
      setCheckingVerification(false);
    }
  };

  const handleSignOut = () => {
    // This would typically call a sign out function
    window.location.reload();
  };

  return (
    <Container>
      <Card
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <IconContainer>
          <Mail size={40} />
        </IconContainer>
        
        <Title>Verify Your Email</Title>
        
        <Subtitle>
          We've sent a verification link to your email address. Please check your inbox and click the link to verify your account.
        </Subtitle>
        
        <EmailAddress>{userEmail}</EmailAddress>
        
        <Instructions>
          <InstructionTitle>What to do next:</InstructionTitle>
          <InstructionList>
            <li>Check your email inbox (and spam folder)</li>
            <li>Click the verification link in the email</li>
            <li>Return to this page and click "Check Verification"</li>
            <li>Once verified, you'll be able to access your account</li>
          </InstructionList>
        </Instructions>
        
        {message && (
          <>
            {messageType === 'success' && (
              <SuccessMessage>
                <CheckCircle size={16} />
                {message}
              </SuccessMessage>
            )}
            {messageType === 'error' && (
              <ErrorMessage>
                <AlertCircle size={16} />
                {message}
              </ErrorMessage>
            )}
            {messageType === 'info' && (
              <InfoMessage>
                <Mail size={16} />
                {message}
              </InfoMessage>
            )}
          </>
        )}
        
        <Button 
          onClick={checkEmailVerification}
          disabled={checkingVerification}
        >
          {checkingVerification ? (
            <>
              <RefreshCw size={16} className="animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <CheckCircle size={16} />
              Check Verification
            </>
          )}
        </Button>
        
        <Button 
          onClick={handleResendVerification}
          disabled={loading}
        >
          {loading ? (
            <>
              <RefreshCw size={16} className="animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Mail size={16} />
              Resend Verification Email
            </>
          )}
        </Button>
        
        <SecondaryButton onClick={handleSignOut}>
          Sign Out
        </SecondaryButton>
      </Card>
    </Container>
  );
};

export default EmailVerificationPending;

